import React, { useState, useEffect } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  CircularProgress,
  Grid,
  List,
  ListItem,
  Paper,
} from '@mui/material'
import {
  getAdvancedReportConfigurationById,
  saveConfigurationRoles,
} from '../services/AdminService'
import ResponsiveAppBar from '../common/ResponsiveAppBar'
import { Assessment } from '@mui/icons-material'
import { DropdownOption } from 'src/viewModels/DropdownOption'
import stores from '../Stores'

interface IAdvancedReportConfigModalProps {
  isOpen: boolean
  id: string
  onClose: () => void
}

const AdvancedReportConfigModal: React.FC<IAdvancedReportConfigModalProps> = ({
  isOpen,
  id,
  onClose,
}) => {
  // State
  const [reportName, setReportName] = useState<string>()
  const [selectedRoleIds, setSelectedRoleIds] = useState<string[]>([])
  const [initialSelectedRoleIds, setInitialSelectedRoleIds] = useState<string[]>([])
  const [activeSelectedRole, setActiveSelectedRole] = useState<DropdownOption | null>(
    null
  )
  const [activeAvailableRole, setActiveAvailableRole] = useState<DropdownOption | null>(
    null
  )
  const [selectedRoles, setSelectedRoles] = useState<DropdownOption[]>([])
  const [availableRoles, setAvailableRoles] = useState<DropdownOption[]>([])
  const [isLoading, setIsLoading] = useState(false)

  useEffect(() => {
    if (isOpen) {
      const loadData = async () => {
        setIsLoading(true)
        getAdvancedReportConfigurationById(id)
          .then((response) => {
            setReportName(response.name)
            setSelectedRoleIds(response.selectedRoles.map((role) => role.id))
            setInitialSelectedRoleIds(response.selectedRoles.map((role) => role.id))
            setSelectedRoles(response.selectedRoles)
            setAvailableRoles(response.availableRoles)
          })
          .finally(() => {
            setIsLoading(false)
          })
      }

      loadData()
    }
  }, [isOpen])

  const handleSubmit = async () => {
    setIsLoading(true)

    saveConfigurationRoles(id, selectedRoleIds)
      .then(() => {
        stores.notifications.sendNotification(
          'save-config-success',
          'Configuration saved successfully',
          { variant: 'success' }
        )
      })
      .finally(() => {
        setIsLoading(false)
        onClose()
      })
  }

  const handleAddRole = (role: DropdownOption) => {
    if (!selectedRoleIds.includes(role.id)) {
      setSelectedRoleIds([...selectedRoleIds, role.id])
      setAvailableRoles((prev) => prev.filter((i) => i.id !== role.id))
      setSelectedRoles((prev) => [...prev, role])
    }
  }

  const handleRemoveRole = (role: DropdownOption) => {
    setSelectedRoleIds(selectedRoleIds.filter((id) => id !== role.id))
    setSelectedRoles((prev) => prev.filter((i) => i.id !== role.id))
    setAvailableRoles((prev) => [...prev, role])
  }

  const selectAvailableRole = (role: DropdownOption) => () => {
    setActiveAvailableRole(role)
  }

  const selectSelectedRole = (role: DropdownOption) => () => {
    setActiveSelectedRole(role)
  }

  const addRole = () => {
    if (activeAvailableRole) {
      handleAddRole(activeAvailableRole)
      setActiveAvailableRole(null)
    }
  }

  const removeRole = () => {
    if (activeSelectedRole) {
      handleRemoveRole(activeSelectedRole)
      setActiveSelectedRole(null)
    }
  }

  // Check if roles have been modified from their initial state
  const hasRolesChanged = () => {
    if (selectedRoleIds.length !== initialSelectedRoleIds.length) {
      return true
    }

    // Check if all current selected roles were in the initial selection
    return !selectedRoleIds.every((roleId) => initialSelectedRoleIds.includes(roleId))
  }

  return (
    <Dialog
      open={isOpen}
      onClose={() => onClose()}
      maxWidth="md"
      sx={{
        '& .MuiDialog-paper': {
          width: 600,
          display: 'flex',
          flexDirection: 'column',
        },
      }}
      fullWidth={false}
    >
      <DialogTitle sx={{ padding: 0 }}>
        <ResponsiveAppBar
          title="Advanced Search Configuration"
          pageIcon={<Assessment />}
          isModal={true}
        />
      </DialogTitle>
      <DialogContent sx={{ padding: '8px 24px 0px' }}>
        <form style={{ width: '100%' }} noValidate>
          <div style={{ marginBottom: 24 }}>
            <span style={{ fontWeight: 'bold' }}>
              Report Name:
              <span
                style={{
                  fontWeight: 'normal',
                  display: 'inline-block',
                  marginLeft: '4px',
                }}
              >
                {reportName}
              </span>
            </span>
          </div>

          <div style={{ borderBottom: '1px solid #CCCCCC', marginBottom: '6px' }}></div>

          <Grid container spacing={0} sx={{ paddingTop: '8px' }}>
            <Grid item xs={5}>
              <div>Available Roles</div>
            </Grid>
            <Grid item xs={2} />
            <Grid item xs={5}>
              <div style={{ paddingBottom: '8px' }}>Selected Roles</div>
            </Grid>

            <Grid item xs={5}>
              <Paper
                elevation={0}
                sx={{
                  border: '#CCCCCC 1px solid',
                  backgroundColor: 'background.paper',
                }}
              >
                <List
                  dense={true}
                  disablePadding={true}
                  sx={{
                    height: '180px',
                    overflowY: 'auto',
                    width: '100%',
                  }}
                >
                  {availableRoles ? (
                    availableRoles.map((role) => (
                      <ListItem
                        key={role.id}
                        button
                        onClick={selectAvailableRole(role)}
                        selected={
                          role.description != undefined && role == activeAvailableRole
                        }
                      >
                        {role.description}
                      </ListItem>
                    ))
                  ) : (
                    <></>
                  )}
                </List>
              </Paper>
            </Grid>

            <Grid item xs={2}>
              <Grid
                container
                direction="column"
                justifyContent="center"
                alignItems="center"
                sx={{ height: '100%' }}
                spacing={2}
              >
                <Grid item>
                  <Button variant="contained" color="primary" onClick={addRole}>
                    &gt;
                  </Button>
                </Grid>
                <Grid item>
                  <Button variant="contained" color="primary" onClick={removeRole}>
                    &lt;
                  </Button>
                </Grid>
              </Grid>
            </Grid>
            <Grid item xs={5}>
              <Paper
                elevation={0}
                sx={{
                  border: '#CCCCCC 1px solid',
                  backgroundColor: 'background.paper',
                }}
              >
                <List
                  dense={true}
                  disablePadding={true}
                  sx={{
                    height: '180px',
                    overflowY: 'auto',
                    width: '100%',
                  }}
                >
                  {selectedRoles ? (
                    selectedRoles.map((role: DropdownOption) => (
                      <ListItem
                        key={role.id}
                        button
                        onClick={selectSelectedRole(role)}
                        selected={role.id != undefined && role == activeSelectedRole}
                      >
                        {role.description}
                      </ListItem>
                    ))
                  ) : (
                    <></>
                  )}
                </List>
              </Paper>
            </Grid>
          </Grid>
        </form>
      </DialogContent>
      <DialogActions sx={{ padding: '16px 8px 8px 8px', marginTop: 0 }}>
        <Button
          onClick={handleSubmit}
          variant="contained"
          color="primary"
          disabled={isLoading || !hasRolesChanged()}
        >
          {isLoading ? <CircularProgress size={24} /> : 'Save'}
        </Button>
        <Button onClick={() => onClose()}>Cancel</Button>
      </DialogActions>
    </Dialog>
  )
}

export default AdvancedReportConfigModal
